import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { listProducts } from '@dukanti/shared';
import type { ProductWithImages } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

/** Stable, serializable query keys for product queries + mutations.
 *  Exported so every hook + mutation in the F-003 bundle uses the same
 *  cache keyspace (see design §6). */
export const productKeys = {
  all: ['products'] as const,
  list: (storeId: string) => ['products', 'list', storeId] as const,
  detail: (productId: string) => ['products', 'detail', productId] as const,
};

/**
 * Hook: list products for the given store. Capped at 50 rows server-side
 * via the service layer. Disabled when `storeId` is null so AuthGuard
 * can render before the store id is known.
 *
 * staleTime matches `useCurrentStore` (30s).
 */
export function useProducts(storeId: string | null): UseQueryResult<ProductWithImages[]> {
  return useQuery({
    queryKey: storeId ? productKeys.list(storeId) : productKeys.all,
    enabled: !!storeId,
    staleTime: 30_000,
    queryFn: async () => {
      // Unreachable defensive guard: React Query never invokes queryFn
      // while `enabled: !!storeId` is false, so the `!storeId` branch
      // can't be hit via the hook's public contract.
      /* v8 ignore next 3 */
      if (!storeId) {
        throw new Error('unreachable: enabled guard should prevent this');
      }
      return listProducts(supabase, storeId);
    },
  });
}
